import React, { useState, useEffect } from "react";
import { Col, Container, Form, Row } from "reactstrap";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import { FormProvider, useForm } from "react-hook-form";
import AuthLeftPanel from "../AuthLeftPanel";
import FormInput from "../../../toolkit/formInput";
import PrimaryButton from "../../../toolkit/primaryButton";
import PageTransition from "../../../toolkit/pageTransition";
import { serviceCaller, ServiceCallerProps } from "../../../Admin/composition";
import { executeSignup, checkSignupInvitation } from "../../../utils/fetch";
import { withRouter, RouterProps } from "../../../utils/withRouter";
import {
  checkboxRules,
  emailRules,
  passwordRules,
} from "../../../utils/validation/validationRules";
import { Link, useSearchParams } from "react-router-dom";
import { MobileLogo, AuthFormCol, AuthContentCol } from "../shared";

interface SignUpForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface SignUpPageProps extends ServiceCallerProps, RouterProps {}

const SignUpPage: React.FC<SignUpPageProps> = ({ config, navigate }) => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteStatus, setInviteStatus] = useState<"checking" | "valid" | "invalid" | "missing">(
    inviteToken ? "checking" : "missing"
  );
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);

  const methods = useForm<SignUpForm>();

  useEffect(() => {
    if (!inviteToken || !config?.authServiceUrl) return;

    checkSignupInvitation(config.authServiceUrl, inviteToken)
      .then(result => {
        if (result.valid && result.email) {
          setInviteStatus("valid");
          setInvitedEmail(result.email);
          methods.setValue("email", result.email);
        } else {
          setInviteStatus("invalid");
        }
      })
      .catch(() => {
        setInviteStatus("invalid");
      });
  }, [inviteToken, config?.authServiceUrl]);

  const onSubmit = async (data: SignUpForm) => {
    if (!config?.authServiceUrl || !inviteToken) return;

    if (data.password !== data.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const signupData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        invitationToken: inviteToken,
      };

      const response = await executeSignup(config.authServiceUrl, signupData);

      if (response.success) {
        navigate(`/signup/email-sent?email=${encodeURIComponent(data.email)}`);
      } else {
        setError(response.message || "Signup failed. Please try again.");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };



  if (inviteStatus === "missing") {
    return (
      <Container fluid className="fullscreenFixed">
        <Row className="h-100">
          <AuthLeftPanel
            title="Easily Track Donation Metrics"
            subtitle="Everything you need in dashboard"
            imageAlt="Sign Up"
          />
          <AuthContentCol className={s.rightPanel}>
            <PageTransition>
              <MobileLogo />
              <Row className="justify-content-center" style={{ marginTop: "20%" }}>
                <Col xs="12" md="8" className="text-center">
                  <h2 className={s.title}>Invitation Required</h2>
                  <p className={s.subtitle}>You need an invitation link to create an account. Please contact your administrator.</p>
                  <Link to="/login" className={s.loginLink}>Back to Login</Link>
                </Col>
              </Row>
            </PageTransition>
          </AuthContentCol>
        </Row>
      </Container>
    );
  }

  if (inviteStatus === "invalid") {
    return (
      <Container fluid className="fullscreenFixed">
        <Row className="h-100">
          <AuthLeftPanel
            title="Easily Track Donation Metrics"
            subtitle="Everything you need in dashboard"
            imageAlt="Sign Up"
          />
          <AuthContentCol className={s.rightPanel}>
            <PageTransition>
              <MobileLogo />
              <Row className="justify-content-center" style={{ marginTop: "20%" }}>
                <Col xs="12" md="8" className="text-center">
                  <h2 className={s.title}>Invitation Expired</h2>
                  <p className={s.subtitle}>This invitation link is no longer valid. Please request a new one from your administrator.</p>
                  <Link to="/login" className={s.loginLink}>Back to Login</Link>
                </Col>
              </Row>
            </PageTransition>
          </AuthContentCol>
        </Row>
      </Container>
    );
  }

  if (inviteStatus === "checking") {
    return (
      <Container fluid className="fullscreenFixed">
        <Row className="h-100">
          <AuthLeftPanel
            title="Easily Track Donation Metrics"
            subtitle="Everything you need in dashboard"
            imageAlt="Sign Up"
          />
          <AuthContentCol className={s.rightPanel}>
            <PageTransition>
              <MobileLogo />
              <Row className="justify-content-center" style={{ marginTop: "20%" }}>
                <Col xs="12" md="8" className="text-center">
                  <h2 className={s.title}>Verifying invitation...</h2>
                </Col>
              </Row>
            </PageTransition>
          </AuthContentCol>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="fullscreenFixed">
      <Row className="h-100">
        <AuthLeftPanel
          title="Easily Track Donation Metrics"
          subtitle="Everything you need in dashboard"
          imageAlt="Sign Up"
        />
        <AuthContentCol className={s.rightPanel}>
          <PageTransition>
          <MobileLogo />
          <Row className="justify-content-center" style={{ marginTop: "10%" }}>
            <Col xs="12" md="12" className={`text-center`}>
              <h2 className={s.title}>Get Started Now</h2>
              {error && (
                <div className={s.errorMessage}>
                  {error}
                </div>
              )}
            </Col>
          </Row>
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
              <Row className="mt-4 justify-content-center">
                <AuthFormCol>
                  <FormInput
                    name="firstName"
                    type="text"
                    icon={"fa-regular fa-user"}
                    rules={{ required: "First name is required" }}
                    placeholder="First Name"
                  />
                </AuthFormCol>
              </Row>
              <Row className="mt-2 justify-content-center">
                <AuthFormCol>
                  <FormInput
                    name="lastName"
                    type="text"
                    icon={"fa-regular fa-user"}
                    rules={{ required: "Last name is required" }}
                    placeholder="Last Name"
                  />
                </AuthFormCol>
              </Row>
              <Row className="mt-2 justify-content-center">
                <AuthFormCol>
                  <FormInput
                    name="email"
                    type="email"
                    rules={emailRules}
                    icon={"fa-regular fa-envelope"}
                    placeholder="Email"
                    disabled={!!invitedEmail}
                  />
                </AuthFormCol>
              </Row>
              <Row className="mt-2 justify-content-center">
                <AuthFormCol>
                  <FormInput
                    name="password"
                    type="password"
                    rules={passwordRules}
                    icon="fa-solid fa-lock"
                    placeholder="Password"
                  />
                </AuthFormCol>
              </Row>
              <Row className="mt-2 justify-content-center">
                <AuthFormCol>
                  <FormInput
                    name="confirmPassword"
                    type="password"
                    rules={{
                      ...passwordRules,
                      validate: (value: string) => {
                        const password = methods.getValues('password');
                        return value === password || 'Passwords do not match';
                      }
                    }}
                    icon="fa-solid fa-lock"
                    placeholder="Confirm password"
                  />
                </AuthFormCol>
              </Row>
              <Row className="mt-4 justify-content-center">
                <AuthFormCol>
                  <FormInput
                    name="agreeToTerms"
                    type="checkbox"
                    checkboxLabel={
                      <span className={s.termAndConditionCheckbox}>
                        By signing up, I agree to the ViewSpark Privacy Policy and Terms & Conditions
                      </span>
                    }
                    rules={checkboxRules}
                  />
                </AuthFormCol>
              </Row>
              <Row className="mt-4 justify-content-center">
                <AuthFormCol>
                  <PrimaryButton
                    type="submit"
                    variant="primary"
                    className={`w-100`}
                    loading={isLoading}
                    loadingText="Creating Account"
                    disabled={isLoading}
                  >
                    Sign Up
                  </PrimaryButton>
                </AuthFormCol>
              </Row>
              <Row className="mt-4 justify-content-center">
                <AuthFormCol className="text-center">
                  <span className={s.loginText}>
                    Already have an account?{" "}
                    <Link to="/login" className={s.loginLink}>
                      Log In
                    </Link>
                  </span>
                </AuthFormCol>
              </Row>
            </Form>
          </FormProvider>
          </PageTransition>
        </AuthContentCol>
      </Row>
    </Container>
  );
};

export default withRouter(serviceCaller(withStyles(s)(SignUpPage)));
